import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, ShieldCheck } from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { ApiError } from '@/lib/api';
import { Button, Field, Input } from '@/components/ui';

/**
 * The operator console's own sign-in.
 *
 * Deliberately austere, and deliberately not linked from anywhere. It carries
 * no product marketing, no sign-up prompt and no link back to the customer
 * app — everything that makes the other login pages welcoming is, here, just
 * a surface for somebody who should not be on this page.
 */
export default function PlatformLogin() {
  const { signInAdmin } = useAuth();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      await signInAdmin(email.trim(), password);
      navigate('/admin');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not sign you in');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-bg">
      <div className="w-full max-w-sm">
        <div className="flex items-center gap-2.5 mb-8">
          <ShieldCheck className="h-6 w-6 text-brand" />
          <span className="font-extrabold tracking-tight text-lg">PROVIA console</span>
        </div>

        <h1 className="text-title">Operator sign in</h1>
        <p className="text-muted mt-2 text-sm leading-relaxed">
          For the people who run PROVIA. If you manage properties, you want the{' '}
          <a href="/login" className="link">manager sign in</a> instead.
        </p>

        <form onSubmit={onSubmit} className="mt-8 space-y-4">
          <Field label="Email">
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="username"
              required
              autoFocus
            />
          </Field>

          <Field label="Password">
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••••••"
              autoComplete="current-password"
              required
            />
          </Field>

          {error && (
            <div className="flex items-start gap-2 text-sm text-danger bg-danger/10
                            border border-danger/25 rounded-xl px-3 py-2.5">
              <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          <Button type="submit" variant="primary" size="lg" className="w-full" loading={loading}>
            Sign in
          </Button>
        </form>

        {/*
          Still no self-service reset: a link emailed to an inbox is a
          reasonable risk for one landlord's records and an unreasonable one
          for every customer's at once. An operator resetting another operator
          is a person vouching rather than a mailbox, which is why that exists
          and this does not.
        */}
        <p className="text-sm text-faint mt-8 leading-relaxed">
          Locked out? Ask another operator to send you a reset link from the console.
        </p>

      </div>
    </div>
  );
}
